import {
    Box,
    CircularProgress,
    Paper,
    TableContainer,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material'
import { useQuery, useMutation } from '@tanstack/react-query'
import DateRangeDisplay from 'app/components/dateRange/dateRangeDisplay/dateRangeDisplay'
import { SearchContainer, SearchInput } from 'app/components/search/Search'
import { dashboardService } from 'core/api/dashboard/dashboardService'
import theme from 'core/theme/theme'
import { formatCurrencyBR, formatDateToSearch } from 'core/utils/globalFunctions'
import dayjs, { Dayjs } from 'dayjs'
import { useState, useMemo } from 'react'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import TableViewIcon from '@mui/icons-material/TableView';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Definição dos tipos de dados - ATUALIZADA
interface ReportProduct {
    totalSoldQuantity: number;
    sku: string;
    priceCost: number;
    productId: number;
    productName: string;
    lastSaleDate: string;
    precoRevenda: number;
    totalPrecoCusto: number;
    totalPrecoRevenda: number;
}

// Função para formatar o nome do produto com a primeira letra maiúscula
const formatProductName = (name: string): string => {
    return name
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

// Função para formatar data
const formatDate = (dateString: string): string => {
    return dayjs(dateString).format('DD/MM/YYYY HH:mm');
}

export default function ReportProducts() {
    // Estado do intervalo de data
    const [initDate, setInitDate] = useState<Dayjs | null | undefined>(
        dayjs().startOf('month')
    )
    const [finalDate, setFinalDate] = useState<Dayjs | null | undefined>(
        dayjs().endOf('month')
    )

    // Estado para controle de visualização
    const [displayMode, setDisplayMode] = useState<'top10' | 'top25' | 'top50' | 'all'>('all');

    // Estados para paginação na tabela
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);

    // Estado para pesquisa de produtos
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Estados para o menu de exportação
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);

    // Busca os dados usando React Query
    const reportProductsQuery = useQuery({
        queryKey: ['reportProducts', initDate, finalDate],
        staleTime: Infinity,
        queryFn: () => {
            // Verificar se as datas inicial e final são do mesmo dia
            const isSameDay = initDate?.isSame(finalDate, 'day');

            // Adiciona 1 dia à data final APENAS se for o mesmo dia
            const adjustedFinalDate = isSameDay ? finalDate?.add(1, 'day') : finalDate;

            return dashboardService.getReportProducts(
                formatDateToSearch(initDate!),
                formatDateToSearch(adjustedFinalDate!)
            );
        }
    });

    // Mutation para exportar relatório
    const exportMutation = useMutation({
        mutationFn: (exportMethod: 'CSV' | 'EXCEL') => {
            // Usar a mesma lógica de ajuste de data do relatório principal
            const isSameDay = initDate?.isSame(finalDate, 'day');
            const adjustedFinalDate = isSameDay ? finalDate?.add(1, 'day') : finalDate;

            return dashboardService.exportProductsReport(
                formatDateToSearch(initDate!),
                formatDateToSearch(adjustedFinalDate!),
                exportMethod
            );
        },
        onSuccess: (base64Data: string, exportMethod: 'CSV' | 'EXCEL') => {
            // Converter base64 para blob
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            // Definir MIME type baseado no formato
            const mimeType = exportMethod === 'CSV'
                ? 'text/csv'
                : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

            const blob = new Blob([byteArray], { type: mimeType });

            // Criar link de download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Definir extensão baseada no formato
            const extension = exportMethod === 'CSV' ? 'csv' : 'xlsx';
            link.download = `relatorio_produtos_vendidos_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.${extension}`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            // Fechar menu
            setAnchorEl(null);
        },
        onError: (error) => {
            console.error('Erro ao exportar relatório:', error);
            // Aqui você pode adicionar uma notificação de erro
        }
    });

    const reportProducts: ReportProduct[] = reportProductsQuery.data || [];

    // Função para alterar o modo de visualização
    const handleDisplayModeChange = (mode: 'top10' | 'top25' | 'top50' | 'all') => {
        setDisplayMode(mode);
        setPage(0); // Resetar para a primeira página quando mudar o modo
    };

    // Funções para paginação
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Função para lidar com a pesquisa
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setPage(0); // Resetar para a primeira página quando pesquisar
    };

    // Função para exportar relatório
    const handleExport = (exportMethod: 'CSV' | 'EXCEL') => {
        exportMutation.mutate(exportMethod);
    };

    // Funções para o menu de exportação
    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    // Interface para os itens filtrados
    interface FilteredProductsResult {
        products: ReportProduct[];
        totalFilteredCount: number;
    }

    // Preparar dados para a tabela com filtros aplicados
    const filteredProducts = useMemo<FilteredProductsResult>(() => {
        if (!reportProducts.length) {
            return {
                products: [],
                totalFilteredCount: 0
            };
        }

        // Aplicar filtro de pesquisa se houver
        let filtered = [...reportProducts];
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(product =>
                formatProductName(product.productName).toLowerCase().includes(term) ||
                product.productId.toString().includes(term) ||
                product.sku.toLowerCase().includes(term)
            );
        }

        // Ordenar por quantidade vendida (maior para menor) como solicitado
        const sortedProducts = filtered.sort(
            (a, b) => b.totalSoldQuantity - a.totalSoldQuantity
        );

        // Total de produtos após filtros para informação ao usuário
        const totalFilteredCount = sortedProducts.length;

        // Aplicar filtro de top N para visualização baseado no displayMode
        let displayProducts = [...sortedProducts];

        if (displayMode !== 'all') {
            const limit = displayMode === 'top10' ? 10 : displayMode === 'top25' ? 25 : 50;
            displayProducts = sortedProducts.slice(0, limit);
        }

        return {
            products: displayProducts,
            totalFilteredCount
        };
    }, [reportProducts, displayMode, searchTerm]);

    // Interface para os totais
    interface TotalsResult {
        totalQuantity: number;
        totalPrecoCusto: number;
        totalPrecoRevenda: number;
    }

    // Calcular totais para exibição no rodapé da tabela usando os dados da API
    const totals = useMemo<TotalsResult>(() => {
        if (!filteredProducts.products || filteredProducts.products.length === 0) {
            return {
                totalQuantity: 0,
                totalPrecoCusto: 0,
                totalPrecoRevenda: 0
            };
        }

        return filteredProducts.products.reduce((acc: TotalsResult, product: ReportProduct) => {
            return {
                totalQuantity: acc.totalQuantity + product.totalSoldQuantity,
                totalPrecoCusto: acc.totalPrecoCusto + product.totalPrecoCusto,
                totalPrecoRevenda: acc.totalPrecoRevenda + product.totalPrecoRevenda
            };
        }, { totalQuantity: 0, totalPrecoCusto: 0, totalPrecoRevenda: 0 });
    }, [filteredProducts.products]);

    return (
        <TableContainer
            sx={{ overflow: 'hidden', padding: 1, height: '100%' }}
            component={Paper}
        >


            {reportProductsQuery.isLoading ? (
                <LoadingWrapper>
                    <CircularProgress />
                </LoadingWrapper>
            ) : reportProductsQuery.isError || !reportProducts.length ? (
                <NoDataMessage>
                    {reportProductsQuery.isError
                        ? 'Erro ao carregar os dados.'
                        : 'Nenhuma informação encontrada para o período selecionado.'}
                </NoDataMessage>
            ) : (
                <Box sx={{ width: '100%', height: '100%' }}>
                    {/* Título do relatório */}
                    <Typography
                        variant="h6"
                        sx={{
                            mb: 2,
                            fontWeight: 'bold',
                            color: theme.COLORS.GRAY3
                        }}
                    >
                        Relatório de Produtos Mais Vendidos
                    </Typography>
                    {/* Área de controle */}
                    <ControlBox>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>

                            <SearchContainer>
                                <SearchIcon
                                    sx={{
                                        color: theme.COLORS.YELLOW2,
                                        width: 20,
                                        height: 20,
                                    }}
                                />
                                <SearchInput
                                    placeholder="Pesquisar por nome ou SKU..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                                <CloseIcon
                                    onClick={() => { setSearchTerm(""); setPage(0) }}
                                    sx={{
                                        color: theme.COLORS.YELLOW2,
                                        width: 20,
                                        height: 20,
                                        cursor: 'pointer',
                                        ":hover": {
                                            color: theme.COLORS.YELLOW
                                        }
                                    }}
                                />
                            </SearchContainer>

                            <Button
                                variant="contained"
                                endIcon={<ArrowDropDownIcon />}
                                onClick={handleOpenMenu}
                                disabled={exportMutation.isPending}
                                sx={{
                                    backgroundColor: theme.COLORS.YELLOW2,
                                    '&:hover': {
                                        backgroundColor: theme.COLORS.YELLOW,
                                    },
                                    '&:disabled': {
                                        backgroundColor: theme.COLORS.GRAY2,
                                    }
                                }}
                            >
                                {exportMutation.isPending ? 'Exportando...' : 'Exportar'}
                            </Button>

                            <Menu
                                anchorEl={anchorEl}
                                open={isMenuOpen}
                                onClose={handleCloseMenu}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <MenuItem onClick={() => handleExport('CSV')} disabled={exportMutation.isPending}>
                                    <ListItemIcon>
                                        <DescriptionIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Exportar CSV</ListItemText>
                                </MenuItem>
                                <MenuItem onClick={() => handleExport('EXCEL')} disabled={exportMutation.isPending}>
                                    <ListItemIcon>
                                        <TableViewIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Exportar Excel</ListItemText>
                                </MenuItem>
                            </Menu>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}>
                            <StyledTypography>Período:</StyledTypography>
                            <DateRangeDisplay
                                typeOfDatePicker={'day'}
                                initialDate={initDate}
                                endDate={finalDate}
                                setInitialDate={setInitDate}
                                setFinalDate={setFinalDate}
                            />
                        </Box>
                    </ControlBox>

                    {/* Tabela de dados */}
                    <TableWrapper>
                        <Table stickyHeader aria-label="relatório de produtos">
                            <TableHead>
                                <TableRow>
                                    <StyledTableHeaderCell width="5%">Posição</StyledTableHeaderCell>
                                    <StyledTableHeaderCell width="10%" align="center">SKU</StyledTableHeaderCell>
                                    <StyledTableHeaderCell width="25%">Produto</StyledTableHeaderCell>
                                    <StyledTableHeaderCell width="12%" align="right">Quantidade vendida</StyledTableHeaderCell>
                                    <StyledTableHeaderCell width="8%" align="right">Custo unitário</StyledTableHeaderCell>
                                    <StyledTableHeaderCell width="8%" align="right">Custo total</StyledTableHeaderCell>
                                    <StyledTableHeaderCell width="8%" align="right">Valor Revenda</StyledTableHeaderCell>
                                    <StyledTableHeaderCell width="8%" align="right">Total Revenda</StyledTableHeaderCell>
                                    <StyledTableHeaderCell width="15%" align="right">Última venda</StyledTableHeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredProducts.products
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((product: ReportProduct, index: number) => (
                                        <TableRow
                                            key={product.productId}
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.COLORS.BACKGROUND
                                                },
                                                '&:hover': {
                                                    backgroundColor: `${theme.COLORS.YELLOW2}22`
                                                }
                                            }}
                                        >
                                            <TableCell align="center">
                                                <PositionBadge position={page * rowsPerPage + index + 1}>
                                                    {page * rowsPerPage + index + 1}
                                                </PositionBadge>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.COLORS.GRAY3 }}>
                                                    {product.sku}
                                                </Typography>
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {formatProductName(product.productName)}
                                            </TableCell>
                                            <TableCell align="center">{product.totalSoldQuantity}</TableCell>
                                            <TableCell align="right">{formatCurrencyBR(product.priceCost)}</TableCell>
                                            <TableCell align="right">{formatCurrencyBR(product.totalPrecoCusto)}</TableCell>
                                            <TableCell align="right">{formatCurrencyBR(product.precoRevenda)}</TableCell>
                                            <TableCell align="right">{formatCurrencyBR(product.totalPrecoRevenda)}</TableCell>
                                            <TableCell align="right">{formatDate(product.lastSaleDate)}</TableCell>
                                        </TableRow>
                                    ))}
                                {filteredProducts.products.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                            Nenhum produto encontrado com os filtros selecionados.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[10, 20, 50, 100]}
                            component="div"
                            count={filteredProducts.products.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Linhas por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                        />
                    </TableWrapper>

                    {/* Rodapé com totais */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: theme.COLORS.BACKGROUND, borderRadius: '4px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Total de produtos vendidos: {totals.totalQuantity}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Custo total de produtos: {formatCurrencyBR(totals.totalPrecoCusto)}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Valor total de revenda: {formatCurrencyBR(totals.totalPrecoRevenda)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}
        </TableContainer>
    )
}

// Componentes estilizados
const StyledBox = styled(Box)`
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 0 0 0 0.5rem !important;
    border: solid 2px ${theme.COLORS.YELLOW2} !important;
    margin-bottom: 1rem !important;
`

const StyledTypography = styled.div`
    color: ${theme.COLORS.YELLOW2} !important;
    font-weight: bold !important;
    font-size: 15px !important;
    text-align: center !important;
`

const LoadingWrapper = styled.div`
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    height: 300px !important;
`

const NoDataMessage = styled.div`
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    height: 300px !important;
    font-size: 15px !important;
    font-weight: bold !important;
    color: ${theme.COLORS.YELLOW2} !important;
`

const ControlBox = styled(Box)`
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 1rem 0 !important;
`

const ActionButton = styled.button`
    background-color: ${theme.COLORS.YELLOW2} !important;
    color: white !important;
    border: none !important;
    border-radius: 4px !important;
    padding: 0.5rem 1rem !important;
    font-weight: bold !important;
    cursor: pointer !important;
    transition: all 0.2s !important;
    
    &:hover {
      opacity: 0.8 !important;
    }
    
    &:disabled {
      background-color: ${theme.COLORS.GRAY3} !important;
      cursor: not-allowed !important;
    }
    
    &.active {
      background-color: ${theme.COLORS.GRAY3} !important;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2) !important;
    }
`

const TableWrapper = styled(Box)`
    height: 730px !important;
    overflow-y: auto !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1) !important;
    border-radius: 4px !important;
`

const StyledTableHeaderCell = styled(TableCell)`
    background-color: ${theme.COLORS.YELLOW2} !important;
    color: white !important;
    font-weight: bold !important;
`

const FilterBox = styled(Box)`
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 1rem 0 !important;
    border-bottom: 1px solid ${theme.COLORS.GRAY1} !important;
    margin-bottom: 1rem !important;
`

const DisplaySelector = styled(Box)`
    display: flex !important;
    align-items: center !important;
    gap: 0.5rem !important;
`

const DisplayLabel = styled.span`
    font-weight: bold !important;
    color: ${theme.COLORS.GRAY3} !important;
`

const PositionBadge = styled.div<{ position: number }>`
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 28px !important;
    height: 28px !important;
    border-radius: 50% !important;
    background-color: ${props =>
        props.position <= 3
            ? theme.COLORS.YELLOW2
            : props.position <= 10
                ? '#7986CB'
                : theme.COLORS.GRAY2
    } !important;
    color: white !important;
    font-weight: bold !important;
    font-size: 12px !important;
`